import { t } from '@glory/i18n';
import { addDays, format } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { CalendarDays, Clock } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useId, useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { Calendar } from '../components/calendar.tsx';
import { Popover, PopoverAnchor, PopoverContent } from '../components/popover.tsx';
import { cn } from '../lib/cn.ts';
import { Field, FieldAction, FieldBox, type FieldPart, inputClass, splitField } from './field.tsx';
import { type BoxProps, draftDescription, useBoxState, useDraft } from './draft.tsx';
import { ListPanel, OptionRow } from './listbox.tsx';
import {
  datePreset,
  type DatePresetId,
  type DateRangeValue,
  formatDateInput,
  formatDateRangeInput,
  fromIsoDate,
  type IsoDate,
  parseDateInput,
  parseDateRangeInput,
  parseTimeInput,
  rangePreset,
  type RangePresetId,
  timeSlots,
  type TimeValue,
  toIsoDate,
} from './parsers/date.ts';

const describeDay = (value: IsoDate) => format(fromIsoDate(value), 'EEEEEE, d MMMM yyyy', { locale: ru });

const DATE_PRESETS: readonly { id: DatePresetId; label: string }[] = [
  { id: 'today', label: t('fields.presetToday') },
  { id: 'tomorrow', label: t('fields.presetTomorrow') },
  { id: 'nextWeek', label: t('fields.presetNextWeek') },
  { id: 'monthEnd', label: t('fields.presetMonthEnd') },
];

const RANGE_PRESETS: readonly { id: RangePresetId; label: string }[] = [
  { id: 'thisWeek', label: t('fields.rangeThisWeek') },
  { id: 'lastWeek', label: t('fields.rangeLastWeek') },
  { id: 'thisMonth', label: t('fields.rangeThisMonth') },
  { id: 'lastMonth', label: t('fields.rangeLastMonth') },
  { id: 'thisQuarter', label: t('fields.rangeThisQuarter') },
  { id: 'lastQuarter', label: t('fields.rangeLastQuarter') },
  { id: 'thisYear', label: t('fields.rangeThisYear') },
  { id: 'lastYear', label: t('fields.rangeLastYear') },
];

function PresetList({
  items,
  onPick,
}: {
  items: readonly { id: string; label: string }[];
  onPick: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-border-soft p-2 sm:w-40 sm:flex-col sm:flex-nowrap sm:border-r sm:border-b-0">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className="rounded-md px-2.5 py-1.5 text-left text-small font-semibold hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
          onClick={() => {
            onPick(item.id);
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

interface PickerBoxProps extends BoxProps {
  readonly inputProps: ReturnType<typeof useDraft>['inputProps'];
  readonly showError: boolean;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  readonly icon: ReactNode;
  readonly iconLabel: string;
  readonly inputMode?: 'text' | 'numeric';
  readonly panel: ReactNode;
  readonly panelClassName?: string;
}

/** Поле ввода с кнопкой, открывающей панель (календарь, список времени), привязанную к рамке. */
function PickerBox({
  inputProps,
  showError,
  open,
  onOpenChange,
  onKeyDown,
  icon,
  iconLabel,
  inputMode = 'text',
  panel,
  panelClassName,
  placeholder,
  size,
  variant,
  className,
  'aria-label': ariaLabel,
  ...state
}: PickerBoxProps) {
  const { control, disabled, readOnly, invalid, locked } = useBoxState(state);
  const input = useRef<HTMLInputElement | null>(null);
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!locked || !next) onOpenChange(next);
      }}
    >
      <PopoverAnchor asChild>
        <FieldBox
          size={size}
          variant={variant}
          disabled={disabled}
          readOnly={readOnly}
          invalid={invalid || showError}
          className={className}
          trailing={
            locked ? undefined : (
              <FieldAction
                label={iconLabel}
                pressed={open}
                onClick={() => {
                  onOpenChange(!open);
                }}
              >
                {icon}
              </FieldAction>
            )
          }
        >
          <input
            ref={input}
            {...control}
            {...inputProps}
            aria-label={ariaLabel}
            aria-invalid={showError || control['aria-invalid']}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            inputMode={inputMode}
            autoComplete="off"
            spellCheck={false}
            className={inputClass}
            onKeyDown={onKeyDown}
          />
        </FieldBox>
      </PopoverAnchor>
      <PopoverContent
        className={cn('w-auto p-0', panelClassName)}
        // Панель закрылась — фокус обратно в поле, чтобы продолжить с клавиатуры.
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          input.current?.focus();
        }}
      >
        {panel}
      </PopoverContent>
    </Popover>
  );
}

export interface DateInputProps extends BoxProps {
  readonly value: IsoDate | null;
  readonly onValueChange: (value: IsoDate | null) => void;
  /** Раньше и позже нельзя: дни в календаре недоступны, ввод — ошибка. */
  readonly min?: IsoDate | undefined;
  readonly max?: IsoDate | undefined;
  /** Быстрые даты рядом с календарём; false — без них. */
  readonly presets?: boolean;
}

function useDateState({ value, onValueChange, min, max, today }: DateInputProps) {
  return useDraft<IsoDate>({
    value,
    onValueChange,
    parse: (text) => parseDateInput(text, today),
    format: formatDateInput,
    validate: (date) =>
      min !== undefined && date < min
        ? t('fields.dateTooEarly', { date: formatDateInput(min) })
        : max !== undefined && date > max
          ? t('fields.dateTooLate', { date: formatDateInput(max) })
          : null,
    invalidMessage: t('fields.dateInvalid'),
  });
}

function DateBox({ draft, props }: { draft: ReturnType<typeof useDateState>; props: DateInputProps }) {
  const {
    value: _value,
    min,
    max,
    presets = true,
    today,
    defaultOpen = false,
    onValueChange: _change,
    ...box
  } = props;
  const [open, setOpen] = useState(defaultOpen);
  const now = today ?? new Date();
  const selected = draft.parsed === null ? undefined : fromIsoDate(draft.parsed);
  const pick = (next: IsoDate) => {
    draft.set(next);
    setOpen(false);
  };
  const disabledDays = [
    ...(min === undefined ? [] : [{ before: fromIsoDate(min) }]),
    ...(max === undefined ? [] : [{ after: fromIsoDate(max) }]),
  ];

  return (
    <PickerBox
      {...box}
      placeholder={box.placeholder ?? t('fields.datePlaceholder')}
      inputProps={draft.inputProps}
      showError={draft.error !== null}
      open={open}
      onOpenChange={setOpen}
      icon={<CalendarDays />}
      iconLabel={t('fields.openCalendar')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && draft.editing) {
          e.preventDefault();
          draft.commit();
        } else if (e.key === 'Escape' && draft.editing) {
          e.stopPropagation();
          draft.revert();
        } else if (e.key === 'ArrowDown' && e.altKey) {
          e.preventDefault();
          setOpen(true);
        } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && draft.parsed !== null && !box.readOnly) {
          // Стрелки листают дни: быстро поправить дату на день-два.
          e.preventDefault();
          draft.set(toIsoDate(addDays(fromIsoDate(draft.parsed), e.key === 'ArrowUp' ? 1 : -1)));
        }
      }}
      panel={
        <div className="flex flex-col sm:flex-row">
          {presets ? (
            <PresetList
              items={DATE_PRESETS}
              onPick={(id) => {
                pick(datePreset(id as DatePresetId, now));
              }}
            />
          ) : null}
          <Calendar
            mode="single"
            required={false}
            selected={selected}
            defaultMonth={selected ?? now}
            today={now}
            disabled={disabledDays}
            autoFocus
            onSelect={(day: Date | undefined) => {
              if (day) pick(toIsoDate(day));
            }}
          />
        </div>
      }
    />
  );
}

/**
 * Дата: пишется как удобно (`28.09`, `завтра`, `пт`, `+3д`) или выбирается в календаре
 * с быстрыми датами. Стрелки вверх/вниз листают дни, Alt+↓ открывает календарь.
 */
export function DateInput(props: DateInputProps) {
  const draft = useDateState(props);
  return <DateBox draft={draft} props={props} />;
}

export interface DateFieldProps extends FieldPart, Omit<DateInputProps, keyof FieldPart> {}

export function DateField(props: DateFieldProps) {
  const [field, input] = splitField(props);
  const draft = useDateState(input);
  return (
    <Field
      {...field}
      error={field.error ?? draft.error}
      description={draftDescription(
        draft.editing,
        draft.problem === null && draft.parsed !== null ? describeDay(draft.parsed) : null,
        t('fields.dateHint'),
        field.description,
      )}
    >
      <DateBox draft={draft} props={input} />
    </Field>
  );
}

export interface DateRangeInputProps extends BoxProps {
  readonly value: DateRangeValue | null;
  readonly onValueChange: (value: DateRangeValue | null) => void;
  readonly presets?: boolean;
}

function useRangeState({ value, onValueChange, today }: DateRangeInputProps) {
  return useDraft<DateRangeValue>({
    value,
    onValueChange,
    parse: (text) => parseDateRangeInput(text, today),
    format: formatDateRangeInput,
    invalidMessage: t('fields.dateRangeInvalid'),
  });
}

function RangeBox({ draft, props }: { draft: ReturnType<typeof useRangeState>; props: DateRangeInputProps }) {
  const { value: _value, presets = true, today, defaultOpen = false, onValueChange: _change, ...box } = props;
  const [open, setOpen] = useState(defaultOpen);
  const now = today ?? new Date();
  const current = draft.parsed;
  const [pending, setPending] = useState<DateRange | undefined>(undefined);
  const [clicks, setClicks] = useState(0);
  const shown: DateRange | undefined =
    pending ?? (current ? { from: fromIsoDate(current.from), to: fromIsoDate(current.to) } : undefined);
  const changeOpen = (next: boolean) => {
    setOpen(next);
    setPending(undefined);
    setClicks(0);
  };
  const pick = (next: DateRangeValue) => {
    draft.set(next);
    changeOpen(false);
  };

  return (
    <PickerBox
      {...box}
      placeholder={box.placeholder ?? t('fields.dateRangePlaceholder')}
      inputProps={draft.inputProps}
      showError={draft.error !== null}
      open={open}
      onOpenChange={changeOpen}
      icon={<CalendarDays />}
      iconLabel={t('fields.openCalendar')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && draft.editing) {
          e.preventDefault();
          draft.commit();
        } else if (e.key === 'Escape' && draft.editing) {
          e.stopPropagation();
          draft.revert();
        } else if (e.key === 'ArrowDown' && e.altKey) {
          e.preventDefault();
          changeOpen(true);
        }
      }}
      panel={
        <div className="flex flex-col sm:flex-row">
          {presets ? (
            <PresetList
              items={RANGE_PRESETS}
              onPick={(id) => {
                pick(rangePreset(id as RangePresetId, now));
              }}
            />
          ) : null}
          <Calendar
            mode="range"
            numberOfMonths={2}
            selected={shown}
            defaultMonth={shown?.from ?? now}
            today={now}
            autoFocus
            onSelect={(range: DateRange | undefined) => {
              // Первое нажатие — начало, второе — конец: только тогда период готов.
              const count = clicks + 1;
              setClicks(count);
              setPending(range);
              if (count >= 2 && range?.from && range.to) {
                pick({ from: toIsoDate(range.from), to: toIsoDate(range.to) });
              }
            }}
          />
        </div>
      }
    />
  );
}

/** Период: `1.09–30.09`, `28–30.09`, `с 1.09 по 5.10` или два нажатия в календаре; пресеты сбоку. */
export function DateRangeInput(props: DateRangeInputProps) {
  const draft = useRangeState(props);
  return <RangeBox draft={draft} props={props} />;
}

export interface DateRangeFieldProps extends FieldPart, Omit<DateRangeInputProps, keyof FieldPart> {}

export function DateRangeField(props: DateRangeFieldProps) {
  const [field, input] = splitField(props);
  const draft = useRangeState(input);
  return (
    <Field
      {...field}
      error={field.error ?? draft.error}
      description={draftDescription(
        draft.editing,
        draft.problem === null && draft.parsed !== null
          ? `${describeDay(draft.parsed.from)} – ${describeDay(draft.parsed.to)}`
          : null,
        t('fields.dateRangeHint'),
        field.description,
      )}
    >
      <RangeBox draft={draft} props={input} />
    </Field>
  );
}

export interface TimeInputProps extends Omit<BoxProps, 'today'> {
  readonly value: TimeValue | null;
  readonly onValueChange: (value: TimeValue | null) => void;
  /** Шаг подсказок и стрелок, минут. */
  readonly step?: number;
}

function useTimeState({ value, onValueChange }: TimeInputProps) {
  return useDraft<TimeValue>({
    value,
    onValueChange,
    parse: parseTimeInput,
    format: (v) => v,
    invalidMessage: t('fields.timeInvalid'),
  });
}

const toMinutes = (value: TimeValue) => Number(value.slice(0, 2)) * 60 + Number(value.slice(3));
const fromMinutes = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

function TimeBox({ draft, props }: { draft: ReturnType<typeof useTimeState>; props: TimeInputProps }) {
  const { value: _value, step = 15, defaultOpen = false, onValueChange: _change, ...box } = props;
  const [open, setOpen] = useState(defaultOpen);
  const slots = timeSlots(step);
  // Подсветка — на ближайшем шаге к значению, без значения — к текущему времени.
  const now = new Date();
  const anchor = draft.parsed === null ? now.getHours() * 60 + now.getMinutes() : toMinutes(draft.parsed);
  const nearest = fromMinutes(Math.floor(anchor / step) * step);

  return (
    <PickerBox
      {...box}
      placeholder={box.placeholder ?? t('fields.timePlaceholder')}
      inputProps={draft.inputProps}
      showError={draft.error !== null}
      open={open}
      onOpenChange={setOpen}
      icon={<Clock />}
      iconLabel={t('fields.chooseTime')}
      inputMode="numeric"
      panelClassName="w-32"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && draft.editing) {
          e.preventDefault();
          draft.commit();
        } else if (e.key === 'Escape' && draft.editing) {
          e.stopPropagation();
          draft.revert();
        } else if (e.key === 'ArrowDown' && e.altKey) {
          e.preventDefault();
          setOpen(true);
        } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && draft.parsed !== null && !box.readOnly) {
          e.preventDefault();
          const shifted = toMinutes(draft.parsed) + (e.key === 'ArrowUp' ? step : -step);
          draft.set(fromMinutes(Math.min(Math.max(shifted, 0), 24 * 60 - 1)));
        }
      }}
      panel={
        <ListPanel
          items={slots}
          getKey={(slot) => slot}
          isSelected={(slot) => slot === draft.parsed}
          initialKey={nearest}
          label={t('fields.chooseTime')}
          onSelect={(slot) => {
            draft.set(slot);
            setOpen(false);
          }}
          renderItem={(slot, row) => <OptionRow label={slot} active={row.active} selected={row.selected} />}
        />
      }
    />
  );
}

/** Время: любое (`9:30`, `930`, `9ч`) или из списка с шагом 15 минут; стрелки — ± шаг. */
export function TimeInput(props: TimeInputProps) {
  const draft = useTimeState(props);
  return <TimeBox draft={draft} props={props} />;
}

export interface TimeFieldProps extends FieldPart, Omit<TimeInputProps, keyof FieldPart> {}

export function TimeField(props: TimeFieldProps) {
  const [field, input] = splitField(props);
  const draft = useTimeState(input);
  return (
    <Field
      {...field}
      error={field.error ?? draft.error}
      description={draftDescription(draft.editing, null, t('fields.timeHint'), field.description)}
    >
      <TimeBox draft={draft} props={input} />
    </Field>
  );
}

/** Дата и время по местному времени: `YYYY-MM-DDTHH:MM`. */
export type DateTimeValue = string;

export interface DateTimeFieldProps extends FieldPart, Omit<BoxProps, keyof FieldPart> {
  readonly value: DateTimeValue | null;
  readonly onValueChange: (value: DateTimeValue | null) => void;
  /** Время, если выбрали только дату. */
  readonly defaultTime?: TimeValue;
  readonly step?: number;
}

/** Дата и время рядом: выбрали дату без времени — подставится `defaultTime`, время без даты — сегодня. */
export function DateTimeField(props: DateTimeFieldProps) {
  const [field, { value, onValueChange, defaultTime = '09:00', step, today, ...box }] = splitField(props);
  const generated = useId();
  const id = field.id ?? `datetime-${generated}`;
  const date = value === null ? null : value.slice(0, 10);
  const time = value === null ? null : value.slice(11, 16);
  const change = (nextDate: IsoDate | null, nextTime: TimeValue | null) => {
    if (nextDate === null) onValueChange(null);
    else onValueChange(`${nextDate}T${nextTime ?? defaultTime}`);
  };
  const dateProps: DateInputProps = {
    ...box,
    value: date,
    today,
    onValueChange: (next) => {
      change(next, time);
    },
  };
  const timeProps: TimeInputProps = {
    ...box,
    id: `${id}-time`,
    'aria-label': t('fields.time'),
    ...(step === undefined ? {} : { step }),
    value: time,
    onValueChange: (next) => {
      change(date ?? toIsoDate(today ?? new Date()), next);
    },
  };
  const dateDraft = useDateState(dateProps);
  const timeDraft = useTimeState(timeProps);
  return (
    <Field
      {...field}
      id={id}
      error={field.error ?? dateDraft.error ?? timeDraft.error}
      description={draftDescription(
        dateDraft.editing,
        dateDraft.problem === null && dateDraft.parsed !== null ? describeDay(dateDraft.parsed) : null,
        t('fields.dateHint'),
        field.description,
      )}
    >
      <div className="flex gap-2">
        <DateBox draft={dateDraft} props={dateProps} />
        <div className="w-28 shrink-0">
          <TimeBox draft={timeDraft} props={timeProps} />
        </div>
      </div>
    </Field>
  );
}
